import { Location } from '@angular/common';
import { Component, inject, OnDestroy } from "@angular/core";
import { NavigationStart } from "@angular/router";
import { ApiLiveStream, ApiPodcast, ApiProduction, ApiShow, ApiUser, ApiVOD } from "@tytapp/api";
import { BillingService } from '@tytapp/billing';
import { AppConfig, CanonicalURLService, formatDurationPT, OpenGraph, PageComponent, SubscriptionSet, Transfer } from "@tytapp/common";
import { ProductionsService } from "@tytapp/common-ui";
import { Asset } from '@tytapp/common/asset';
import { environment } from '@tytapp/environment';
import { isClientSide } from '@tytapp/environment-utils';
import { LiveStreamsService } from '@tytapp/live-streams';
import { DownloadsService, MEDIA_TYPE_HELPER, Playlist, PlaylistType } from "@tytapp/media-playback";
import { ShowsService } from '@tytapp/shows';
import { UserService } from "@tytapp/user";

@Component({
    selector: 'tyt-watch-page',
    templateUrl: './watch-page.component.html',
    styleUrls: ['./watch-page.component.scss']
})
export class WatchPageComponent extends PageComponent implements OnDestroy {
    private productionsService = inject(ProductionsService);
    private liveStreamsService = inject(LiveStreamsService);
    private userService = inject(UserService);
    private billing = inject(BillingService);
    private appConfig = inject(AppConfig);
    private opengraph = inject(OpenGraph);
    private showsService = inject(ShowsService);
    private location = inject(Location);
    private canonicalService = inject(CanonicalURLService);
    private downloads = inject(DownloadsService);

    readonly MediaTypes = MEDIA_TYPE_HELPER;

    get pageComponentId() {
        return 'watch';
    }

    @Transfer()
    production: ApiProduction;

    @Transfer()
    show: ApiShow;

    @Transfer()
    activeLiveStreams: ApiLiveStream[];

    @Transfer()
    activeLiveStream: ApiLiveStream;

    @Transfer()
    mode: PlaylistType;

    currentItem: ApiVOD | ApiPodcast;

    @Transfer()
    user: ApiUser;

    get playlist(): (ApiVOD | ApiPodcast)[] {
        if (!this.production)
            return [];

        if (this.mode === 'clips')
            return this.production.vod_clips ?? [];
        else if (this.mode === 'podcasts')
            return this.production.full_length_podcasts ?? [];
        else if (this.mode === 'episodes')
            return this.production.full_length_vods ?? [];

        return [];
    }

    @Transfer()
    startingVodID: any;

    subscriptions: SubscriptionSet = new SubscriptionSet();

    audienceType: string = 'visitor';

    get loginOrJoinUrl() {
        if (!this.user)
            return `/login`;
        return `/join`;
    }

    entitled = false;

    destroy() {
        this.subscriptions.unsubscribeAll();
    }

    @Transfer()
    state = 'loading';

    @Transfer()
    notFoundMessage = "We couldn't find the media you tried to watch.";

    @Transfer()
    notFoundTitle = "404";

    get showDevTools() {
        return environment.showDevTools;
    }

    get downloadsEnabled() {
        return this.downloads.enabled;
    }

    async init() {
        this.subscriptions.subscribe(this.billing.entitlementChanged, entitled => this.entitled = entitled);
        this.subscriptions.subscribe(this.userService.userChanged, user => this.user = user);

        this.route.paramMap.subscribe(async params => {
            this.state = 'loading';

            let productionID = params.get('production');
            this.mode = <PlaylistType>params.get('mode') ?? 'episodes';
            if (this.mode as any === 'watch') this.mode = 'episodes';

            this.startingVodID = params.get('vod');

            if (!['podcasts', 'clips', 'episodes'].includes(this.mode)) {
                // Probably /:show/:productionID
                productionID = this.mode;
                this.mode = 'episodes';
            }

            const mediaUrl = params.get('newUrlStructures') ?
                {
                    show: params.get('show'),
                    productionDate: params.get('productionDate')
                } :
                null;

            if (mediaUrl) {
                productionID = `${ mediaUrl.show }:${ mediaUrl.productionDate }`;
            }

            if (!this.hasServerState) {
                await this.userService.ready;

                try {
                    this.production = await this.productionsService.get(productionID);
                } catch (e) {
                    if (e.statusCode === 404 || e.error === 'not-found') {
                        let code = 'not-found';
                        if (e.json)
                            code = e.json().code || code;

                        if (code === 'unavailable') {
                            this.notFoundTitle = 'Not Available';
                            this.state = 'not-found';
                            this.notFoundMessage = "This content is not currently available.";
                        } else {
                            this.logger.error(`[Watch page] Production ${productionID} not found`);
                            this.notFoundTitle = '404';
                            this.state = 'not-found';
                            this.notFoundMessage = "We couldn't find the media you wanted to watch.";
                        }
                    } else {
                        this.logger.error(`[Watch page] Failed to fetch production:`);
                        this.logger.error(e);

                        this.state = 'not-found';
                        this.notFoundTitle = 'Uh oh';
                        this.notFoundMessage = "An error prevents this content from loading.";
                    }
                }

                if (!this.production)
                    return;

                this.show = await this.fetchShow();
            }

            this.activeLiveStreams ??= await this.liveStreamsService.active() as ApiLiveStream[];
            this.activeLiveStream = this.activeLiveStreams.find(x => x.active_live_event && x.active_live_event.show && x.active_live_event.show.id == this.production.show.id);

            if (this.isProductionEmpty()) {
                this.state = 'empty';
                this.pageTitle = `No Content`;
                return;
            }

            if (await this.redirectIfBonusEpisode()) return;

            this.setCurrentItem(
                this.playlist
                    .find(x => this.startingVodID ? [x.id, x['slug']].includes(this.startingVodID) : true),
                false
            );

            if (!this.currentItem) {
                this.state = 'not-found';
                this.notFoundMessage = `We couldn't find the media item you wanted to watch.`;

                if (environment.showDevTools)
                    this.notFoundMessage += ` [dev:  production ${this.production?.id ?? '<none>'}, mode ${this.mode ?? '<non>'}, item ${this.startingVodID || '<no-id>'}]`;
                return;
            }

            // Setting Canonical URL
            const canonicalUrl = await this.productionsService.getWatchUrl(this.production, this.mode, this.currentItem);
            if (this.canonicalService.redirectToCanonical(canonicalUrl))
                return;

            await this.addLdJson();

            this.pageTitle = this.currentItem?.description ?? `${this.production.show.name} ${this.production.date_simple}`;
            this.state = 'ready';
        });

        this.audienceType = this.userService.audienceType;
    }

    private isProductionEmpty() {
        if (!this.production)
            return true;

        return this.production.full_length_podcasts.length === 0
            && this.production.full_length_vods.length === 0
            && this.production.vod_clips.length === 0
        ;
    }

    private async fetchShow() {
        if (!this.production?.show)
            return null;

        try {
            return await this.showsService.get(this.production.show.id);
        } catch (e) {
            this.logger.error("Failed to fetch full Show record for production:");
            this.logger.error(e);
            return this.production.show;
        }

    }

    private async addLdJson() {
        let item = this.currentItem;

        if (item.type === 'vod') {
            const vod = item as ApiVOD;
            let selfUrl = this.productionsService.getWatchUrlSync(this.production, this.mode, item, true);

            this.opengraph.setMetadata({
                title: item.title,
                description: item.description,
                type: 'video.movie',
                url: selfUrl,
                siteName: 'TYT.com',
                image: vod.thumbnail_landscape1
            });

            let exposeVideoInLD = await this.appConfig.featureEnabled('apps.web.ld_expose_video');
            let exposeVideoContentInLD = await this.appConfig.featureEnabled('apps.web.ld_expose_video_content');

            if (exposeVideoInLD) {
                try {
                    this.opengraph.setLdJson({
                        "@context": "http://schema.org",
                        "@type": "VideoObject",
                        url: selfUrl,
                        name: item.title || item.description,
                        description: item.description,
                        width: 1920,
                        height: 1080,
                        playerType: "HLS",
                        videoQuality: "HD",
                        thumbnailUrl: vod.thumbnail_landscape1 || this.production?.thumbnail_landscape1,
                        contentUrl: item.asset && exposeVideoContentInLD ? item.asset.url : undefined,
                        duration: formatDurationPT(parseInt(<any>item.duration)),
                        uploadDate: new Date(this.production.date).toISOString(),
                        isAccessibleForFree: vod.is_premium ? 'False' : 'True',
                        hasPart: {
                            '@type': "WebPageElement",
                            isAccessibleForFree: vod.is_premium ? 'False' : 'True',
                            cssSelector: "tyt-media-player"
                        }
                    });
                } catch (e) {
                    this.logger.error(`Failed to set LD+JSON for video:`);
                    this.logger.error(e);
                }
            }
        }
    }
    setCurrentMode(mode: Playlist) {
        if (!mode)
            return;
        this.mode = mode;
        this.updateUrl();
    }

    setCurrentItem(item: ApiVOD | ApiPodcast, shouldUpdateUrl = true) {
        if (!item)
            return;
        this.currentItem = item;
        this.pageTitle = this.currentItem?.description;
        if (shouldUpdateUrl)
            this.updateUrl();
        if ((item as ApiVOD).kind === 'live_replay') {
            this.addNoIndexMetaTag();
        }
    }

    updateUrlTimeout;
    updateUrl() {
        if (!this.currentItem || !isClientSide())
            return;

        clearTimeout(this.updateUrlTimeout);
        this.updateUrlTimeout = setTimeout(() => {
            this.location.replaceState(
                this.productionsService.getWatchUrlSync(this.production, this.mode, this.currentItem)
            );
        }, 100);
    }

    async redirectIfBonusEpisode(): Promise<boolean> {
        const assetID = this.production.internal_name;
        const asset = Asset.parseID(assetID);

        if (asset.showCode !== 'BE') return false;

        const production = await this.productionsService.get(`the-young-turks:${ asset.strDate }`);

        if (production) {
            const playlist = this.mode === 'podcasts' ? production.full_length_podcasts : production.full_length_vods;

            const media = playlist.find(value => value.internal_name === assetID);

            if (media) {
                this.redirection.go(this.productionsService.getWatchUrlSync(production, this.mode, media), true);
                return true;
            }
        }

        return false;
    }

    addNoIndexMetaTag() {
        const tag = this.meta.updateTag({ name: 'robots', content: 'noindex' });
        if (tag) {
            const subscription = this.router.events.subscribe(event => {
                if (event instanceof NavigationStart) {
                    this.meta.removeTagElement(tag);
                    subscription.unsubscribe();
                }
            })
        }
    }
}
